import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { api, mediaUrl } from '../lib/api'
import { useAuth } from '../lib/auth'
import {
  avatarFor,
  blueOf,
  METHOD_OPTIONS,
  redOf,
  roundsForBout,
  SECTION_LABELS,
  type Fixture,
} from '../lib/fighters'

type PoolEntry = {
  userId: string
  username: string
  displayName: string
  avatarUrl?: string | null
  pickStatus: string
  isSelf: boolean
  picksRevealed: boolean
  picks: Record<string, string> | null
  submittedAt?: string | null
}

type OddsSide = {
  entrantId: string
  name: string
  price: number
  pct: number
  american: string
}

type BoutOdds = {
  fixtureId: string
  missing: boolean
  closed: boolean
  resolved: boolean
  moneyline: {
    red: OddsSide | null
    blue: OddsSide | null
    favorite: 'red' | 'blue' | null
  }
  methods: {
    koTko: number | null
    submission: number | null
    decision: number | null
  }
  goTheDistance: number | null
  polymarketUrl: string | null
}

function pctLabel(p: number | null | undefined): string {
  if (p == null || !Number.isFinite(p)) return '—'
  return `${Math.round(p * 100)}%`
}

function labelPick(fixtures: Fixture[], key: string, value: string): string {
  const methodMatch = key.match(/^(.+)-method$/)
  if (methodMatch) return value
  const roundMatch = key.match(/^(.+)-round$/)
  if (roundMatch) return value
  const f = fixtures.find((x) => x.fixtureId === key)
  if (!f) return value
  const red = redOf(f)
  const blue = blueOf(f)
  if (value === red.id) return red.name
  if (value === blue.id) return blue.name
  return value
}

export function PicksPage() {
  const { id } = useParams()
  const { token } = useAuth()
  const [fixtures, setFixtures] = useState<Fixture[]>([])
  const [picks, setPicks] = useState<Record<string, string>>({})
  const [status, setStatus] = useState<'none' | 'draft' | 'submitted'>('none')
  const [msg, setMsg] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [competitionId, setCompetitionId] = useState<string | null>(null)
  const [eventTitle, setEventTitle] = useState('Pick workbench')
  const [entries, setEntries] = useState<PoolEntry[]>([])
  const [viewerSubmitted, setViewerSubmitted] = useState(false)
  const [canRevealOthers, setCanRevealOthers] = useState(false)
  const [loading, setLoading] = useState(true)
  const [oddsByFixture, setOddsByFixture] = useState<Record<string, BoutOdds>>({})
  const [oddsAt, setOddsAt] = useState<string | null>(null)
  const [oddsError, setOddsError] = useState<string | null>(null)

  const loadBoard = useCallback(async () => {
    if (!id) return
    const board = await api<{
      viewerSubmitted: boolean
      canRevealOthers: boolean
      entries: PoolEntry[]
    }>(`/api/pools/${id}/entries`, { token })
    setViewerSubmitted(board.viewerSubmitted)
    setCanRevealOthers(board.canRevealOthers)
    setEntries(board.entries || [])
  }, [id, token])

  const loadOdds = useCallback(
    async (compId: string) => {
      try {
        const board = await api<{ fetchedAt: string; bouts: BoutOdds[] }>(
          `/api/competitions/${compId}/odds/polymarket`,
          { token }
        )
        const map: Record<string, BoutOdds> = {}
        for (const b of board.bouts || []) map[b.fixtureId] = b
        setOddsByFixture(map)
        setOddsAt(board.fetchedAt)
        setOddsError(null)
      } catch (e: any) {
        setOddsError(e.message || 'Odds unavailable')
      }
    },
    [token]
  )

  useEffect(() => {
    ;(async () => {
      setLoading(true)
      setError(null)
      try {
        const pools = await api<{ pools: any[] }>('/api/pools', { token })
        const pool = pools.pools.find((p) => p.id === id) || pools.pools[0]
        if (!pool) {
          setError('Pool not found')
          return
        }
        setCompetitionId(pool.competitionId)
        const comp = await api<{ competition: any }>(`/api/competitions/${pool.competitionId}`, {
          token,
        })
        setFixtures(comp.competition.fixtures || [])
        setEventTitle(comp.competition.title || 'Pick workbench')

        const mine = await api<{
          joined: boolean
          submission: {
            picks: Record<string, string>
            status: string
          } | null
        }>(`/api/pools/${id}/picks/me`, { token })
        if (mine.submission?.picks) {
          setPicks(mine.submission.picks as Record<string, string>)
        }
        if (mine.submission?.status === 'submitted') setStatus('submitted')
        else if (mine.submission?.status === 'draft') setStatus('draft')
        else setStatus('none')

        await loadBoard()
        await loadOdds(pool.competitionId)
      } catch (e: any) {
        setError(e.message)
      } finally {
        setLoading(false)
      }
    })()
  }, [id, token, loadBoard, loadOdds])

  // Refresh live odds every 30s
  useEffect(() => {
    if (!competitionId) return
    const t = setInterval(() => {
      loadOdds(competitionId)
    }, 30_000)
    return () => clearInterval(t)
  }, [competitionId, loadOdds])

  const bySection = useMemo(() => {
    const map = new Map<string, Fixture[]>()
    for (const f of fixtures) {
      const sec = f.section || 'main'
      if (!map.has(sec)) map.set(sec, [])
      map.get(sec)!.push(f)
    }
    return map
  }, [fixtures])

  const locked = status === 'submitted'

  function setWinner(fixtureId: string, entrantId: string) {
    if (locked) return
    setPicks((p) => ({ ...p, [fixtureId]: entrantId }))
  }
  function setProp(fixtureId: string, prop: string, value: string) {
    if (locked) return
    setPicks((p) => {
      const next = { ...p, [`${fixtureId}-${prop}`]: value }
      if (prop === 'method' && value === 'Decision') {
        next[`${fixtureId}-round`] = 'Decision'
      }
      return next
    })
  }

  async function saveDraft() {
    setError(null)
    try {
      await api(`/api/pools/${id}/picks/draft`, { method: 'PUT', token, body: { picks } })
      setStatus((s) => (s === 'submitted' ? 'submitted' : 'draft'))
      setMsg('Draft saved — you can reload and still see your picks')
      await loadBoard()
    } catch (e: any) {
      setError(e.message || JSON.stringify(e.body))
    }
  }

  async function submit() {
    setError(null)
    try {
      await api(`/api/pools/${id}/picks/submit`, {
        method: 'POST',
        token,
        body: { picks },
        idempotencyKey: `submit-${id}-${Date.now()}`,
      })
      setStatus('submitted')
      setMsg(
        'Picks submitted and saved. Other players’ picks are now visible to you; yours are visible to them once they submit.'
      )
      await loadBoard()
    } catch (e: any) {
      setError(e.message || JSON.stringify(e.body))
    }
  }

  const picked = fixtures.filter((f) => picks[f.fixtureId]).length
  const methodsPicked = fixtures.filter((f) => picks[`${f.fixtureId}-method`]).length
  const roundsPicked = fixtures.filter((f) => picks[`${f.fixtureId}-round`]).length

  const others = entries.filter((e) => !e.isSelf)
  const revealedOthers = others.filter((e) => e.picksRevealed && e.picks)

  if (loading) {
    return <div className="panel">Loading pick workbench…</div>
  }

  return (
    <div className="stack">
      <div className="panel">
        <div className="row" style={{ justifyContent: 'space-between' }}>
          <div>
            <h2 style={{ margin: 0 }}>{eventTitle}</h2>
            <p className="muted" style={{ margin: '4px 0 0' }}>
              Full card · pick <strong>winner</strong>, <strong>method</strong> (KO/TKO · Submission ·
              Decision), and <strong>round</strong>. Live odds from Polymarket.
            </p>
            <p className="muted" style={{ margin: '4px 0 0' }}>
              Progress: {picked}/{fixtures.length} winners · {methodsPicked} methods · {roundsPicked}{' '}
              rounds
              {status === 'submitted' && (
                <span className="badge-submitted"> · Submitted (locked for you)</span>
              )}
              {status === 'draft' && <span> · Draft saved</span>}
              {oddsAt && (
                <span>
                  {' '}
                  · Odds {new Date(oddsAt).toLocaleTimeString()}
                  {oddsError ? ' (stale)' : ''}
                </span>
              )}
            </p>
          </div>
          <div className="row" style={{ gap: 8 }}>
            {competitionId && (
              <button
                type="button"
                className="btn secondary"
                onClick={() => competitionId && loadOdds(competitionId)}
              >
                Refresh odds
              </button>
            )}
            {competitionId && (
              <Link className="btn secondary" to={`/events/${competitionId}`}>
                Back to event
              </Link>
            )}
          </div>
        </div>
        {error && (
          <div className="error" style={{ marginTop: 10 }}>
            {error}
          </div>
        )}
        {msg && (
          <div className="success" style={{ marginTop: 10 }}>
            {msg}
          </div>
        )}
      </div>

      <div className="panel pool-board">
        <h3 style={{ marginTop: 0 }}>Pool board</h3>
        <p className="muted" style={{ marginTop: 0 }}>
          {canRevealOthers
            ? 'You submitted — other players’ submitted picks are revealed below.'
            : 'Submit your picks to unlock everyone else’s card. Status shows who is still drafting.'}
        </p>
        <div className="entry-list">
          {entries.length === 0 ? (
            <p className="muted">No one has joined this pool yet.</p>
          ) : (
            entries.map((e) => (
              <div key={e.userId} className={`entry-row${e.isSelf ? ' self' : ''}`}>
                <div className="entry-who">
                  {e.avatarUrl ? (
                    <img className="entry-avatar" src={mediaUrl(e.avatarUrl) || ''} alt="" />
                  ) : (
                    <span className="entry-avatar placeholder">
                      {(e.displayName || e.username).slice(0, 1).toUpperCase()}
                    </span>
                  )}
                  <div>
                    <strong>
                      {e.displayName || e.username}
                      {e.isSelf ? ' (you)' : ''}
                    </strong>
                    <div className="sub muted">
                      {e.pickStatus === 'submitted'
                        ? 'Submitted'
                        : e.pickStatus === 'draft'
                          ? 'Drafting'
                          : 'Joined · no picks yet'}
                    </div>
                  </div>
                </div>
                <div className="entry-picks">
                  {e.picksRevealed && e.picks ? (
                    <div className="pick-chips">
                      {fixtures.map((f) => {
                        const w = e.picks![f.fixtureId]
                        if (!w) return null
                        return (
                          <span key={f.fixtureId} className="pick-chip" title={f.title || f.fixtureId}>
                            {labelPick(fixtures, f.fixtureId, w)}
                            {e.picks![`${f.fixtureId}-method`]
                              ? ` · ${e.picks![`${f.fixtureId}-method`]}`
                              : ''}
                            {e.picks![`${f.fixtureId}-round`]
                              ? ` · ${e.picks![`${f.fixtureId}-round`]}`
                              : ''}
                          </span>
                        )
                      })}
                    </div>
                  ) : e.pickStatus === 'submitted' ? (
                    <span className="muted">Hidden until you submit</span>
                  ) : (
                    <span className="muted">—</span>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
        {!viewerSubmitted && others.some((o) => o.pickStatus === 'submitted') && (
          <p className="muted" style={{ marginBottom: 0, marginTop: 10 }}>
            {others.filter((o) => o.pickStatus === 'submitted').length} player(s) already submitted —
            submit yours to see their picks.
          </p>
        )}
        {viewerSubmitted && revealedOthers.length === 0 && others.length > 0 && (
          <p className="muted" style={{ marginBottom: 0, marginTop: 10 }}>
            Waiting on friends to submit…
          </p>
        )}
      </div>

      {(['main', 'prelim', 'early'] as const)
        .filter((sec) => bySection.has(sec))
        .concat(
          [...bySection.keys()].filter((s) => !['main', 'prelim', 'early'].includes(s)) as any
        )
        .map((sec) => {
          const list = bySection.get(sec)!
          return (
            <div key={sec}>
              <div className="section-label">
                {SECTION_LABELS[sec] || sec}
                <span className="muted" style={{ fontWeight: 500, marginLeft: 8 }}>
                  {list.length} bout{list.length === 1 ? '' : 's'}
                </span>
              </div>
              {list.map((f) => {
                const red = redOf(f)
                const blue = blueOf(f)
                const method = picks[`${f.fixtureId}-method`] || ''
                const round = picks[`${f.fixtureId}-round`] || ''
                const roundOpts = roundsForBout(f.rounds)
                const odds = oddsByFixture[f.fixtureId]
                const redOdds = odds?.moneyline.red
                const blueOdds = odds?.moneyline.blue
                return (
                  <div key={f.fixtureId} className="panel pick-card" style={{ marginBottom: 10 }}>
                    <div
                      className="bout"
                      style={{ border: 'none', background: 'transparent', padding: 0, margin: 0 }}
                    >
                      <div className="corner red">
                        <img
                          className="avatar fighter-cutout"
                          src={avatarFor(red.id, red.image)}
                          alt={red.name}
                        />
                        <div>
                          <div className="name">{red.name}</div>
                          {red.nickname && <div className="nick">“{red.nickname}”</div>}
                          {red.record && <div className="record">{red.record}</div>}
                          {redOdds && (
                            <div
                              className={`pm-odds ${odds?.moneyline.favorite === 'red' ? 'fav' : ''}`}
                            >
                              <span className="pm-american">{redOdds.american}</span>
                              <span className="pm-pct">{redOdds.pct}%</span>
                            </div>
                          )}
                          <button
                            type="button"
                            className={`tbtn pick-fighter red ${picks[f.fixtureId] === red.id ? 'selected' : ''}`}
                            onClick={() => setWinner(f.fixtureId, red.id)}
                            disabled={locked}
                          >
                            Winner
                          </button>
                        </div>
                      </div>
                      <div className="mid">
                        {f.billing && <div className="billing">{f.billing}</div>}
                        <div className="vs">VS</div>
                        <div className="weight">
                          {f.weightClass} · {f.rounds || 3}R
                        </div>
                        {odds && !odds.missing && (
                          <div className="pm-live-tag" title="Polymarket implied probability">
                            {odds.resolved ? 'Resolved' : odds.closed ? 'Closed' : 'Live PM'}
                          </div>
                        )}
                      </div>
                      <div className="corner blue">
                        <img
                          className="avatar fighter-cutout"
                          src={avatarFor(blue.id, blue.image)}
                          alt={blue.name}
                        />
                        <div>
                          <div className="name">{blue.name}</div>
                          {blue.nickname && <div className="nick">“{blue.nickname}”</div>}
                          {blue.record && <div className="record">{blue.record}</div>}
                          {blueOdds && (
                            <div
                              className={`pm-odds ${odds?.moneyline.favorite === 'blue' ? 'fav' : ''}`}
                            >
                              <span className="pm-american">{blueOdds.american}</span>
                              <span className="pm-pct">{blueOdds.pct}%</span>
                            </div>
                          )}
                          <button
                            type="button"
                            className={`tbtn pick-fighter ${picks[f.fixtureId] === blue.id ? 'selected' : ''}`}
                            onClick={() => setWinner(f.fixtureId, blue.id)}
                            disabled={locked}
                          >
                            Winner
                          </button>
                        </div>
                      </div>
                    </div>

                    {odds && !odds.missing && (
                      <div className="pm-method-odds">
                        <span>
                          KO/TKO <strong>{pctLabel(odds.methods.koTko)}</strong>
                        </span>
                        <span>
                          Sub <strong>{pctLabel(odds.methods.submission)}</strong>
                        </span>
                        <span>
                          Distance <strong>{pctLabel(odds.methods.decision ?? odds.goTheDistance)}</strong>
                        </span>
                        {odds.polymarketUrl && (
                          <a
                            className="pm-link"
                            href={odds.polymarketUrl}
                            target="_blank"
                            rel="noreferrer"
                          >
                            Polymarket ↗
                          </a>
                        )}
                      </div>
                    )}
                    {odds?.missing && (
                      <div className="pm-method-odds muted">No Polymarket market for this bout yet</div>
                    )}

                    <div className="prop-block">
                      <div className="prop-label">Method of victory</div>
                      <div className="prop-btns">
                        {METHOD_OPTIONS.map((m) => (
                          <button
                            key={m}
                            type="button"
                            className={`tbtn prop-btn ${method === m ? 'selected' : ''}`}
                            onClick={() => setProp(f.fixtureId, 'method', m)}
                            disabled={locked}
                          >
                            {m}
                            {m === 'KO/TKO' && odds?.methods.koTko != null
                              ? ` · ${pctLabel(odds.methods.koTko)}`
                              : ''}
                            {m === 'Submission' && odds?.methods.submission != null
                              ? ` · ${pctLabel(odds.methods.submission)}`
                              : ''}
                            {m === 'Decision' &&
                            (odds?.methods.decision != null || odds?.goTheDistance != null)
                              ? ` · ${pctLabel(odds.methods.decision ?? odds.goTheDistance)}`
                              : ''}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="prop-block">
                      <div className="prop-label">Round / decision</div>
                      <div className="prop-btns">
                        {roundOpts.map((r) => (
                          <button
                            key={r}
                            type="button"
                            className={`tbtn prop-btn ${round === r ? 'selected' : ''}`}
                            onClick={() => setProp(f.fixtureId, 'round', r)}
                            disabled={locked || (method === 'Decision' && r !== 'Decision')}
                          >
                            {r}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )
        })}

      <div className="panel row">
        <button className="btn secondary" onClick={saveDraft} disabled={locked}>
          Save draft
        </button>
        <button className="btn" onClick={submit} data-testid="submit-picks" disabled={locked}>
          {locked ? 'Picks submitted' : 'Submit picks'}
        </button>
        <span className="muted">
          {locked
            ? 'Your card is saved. Reload anytime to review it; friends see it after they submit.'
            : 'Every bout needs a winner. Method and round are optional but score when correct (1 pt each).'}
        </span>
      </div>
    </div>
  )
}
